import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { PlusCircle as PlusCircleIcon } from 'react-feather'
import { Box, Button, Grid, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import PropTypes from 'prop-types'

import { LinkedDocumentList, LinkedDocumentModal } from 'components'

import useDocuments from 'hooks/useDocuments'
import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'

import constants from 'constants/index'
import * as service from 'service'

const DocumentsSection = ({ dataProcessId }) => {
  const snackbar = useSnackbar()
  const [openModal, setOpenModal] = useState(false)
  const {
    data: documentData,
    setData,
    linkedDocuments,
    setLinkedDocuments,
    setLoading,
    loadData,
  } = useDocuments()

  const { PERMITTED_DOCUMENTS_TYPE, PERMITTED_DOCUMENTS_MIME_TYPES } =
    constants.dataProcess

  const items = helpers.dataProcess.buildDocumentItems(
    documentData,
    linkedDocuments,
  )

  const excludedIds = (linkedDocuments || []).map(
    (d) => d.documentVersion?.privacyPolicyId,
  )

  const handleModalSubmit = async (attachments, privacyPolicyIds) => {
    const hasAttachments = !isEmpty(attachments)
    const hasLinkedDocs = !isEmpty(privacyPolicyIds)

    if (!hasAttachments && !hasLinkedDocs) return

    try {
      setLoading(true)

      if (hasAttachments) {
        await service.dponet.documents.addDocument({
          dataProcessId,
          files: attachments,
        })
      }

      if (hasLinkedDocs) {
        await service.dponet.dataProcesses.addLinkedDocument({
          dataProcessId,
          privacyPolicyIds: privacyPolicyIds.map((doc) => doc.id),
        })
      }

      await loadData(dataProcessId)
      snackbar.open({
        message: 'Documento(s) adicionado(s) com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || 'Ocorreu um erro ao adicionar o(s) documento(s)',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveItem = async (item) => {
    setLoading(true)
    try {
      if (item._type === 'document') {
        await service.dponet.dataProcesses.removeLinkedDocument({
          dataProcessId,
          documentId: item.id,
        })
        setLinkedDocuments((prev) => prev.filter((d) => d.id !== item.id))
      } else {
        await service.dponet.documents.deleteDocument({
          dataProcessesId: dataProcessId,
          documentName: item.name,
        })
        setData(
          (documentData || []).filter((doc) => doc?.filename !== item.name),
        )
      }
      snackbar.open({
        message: 'Documento removido com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu algum erro ao deletar o documento!',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Grid container justifyContent="space-between">
        <Box mt={2}>
          <Typography variant="h5">Documentos</Typography>
          <Typography variant="body2">
            Arquivos suportados:&nbsp;
            {PERMITTED_DOCUMENTS_TYPE}
          </Typography>
        </Box>
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<PlusCircleIcon size={20} />}
            size="small"
            onClick={() => setOpenModal(true)}
          >
            <Typography>Adicionar documentos</Typography>
          </Button>
        </Box>
      </Grid>
      <Box py={2}>
        <LinkedDocumentList
          items={items}
          onRemove={handleRemoveItem}
          showDocumentId
        />
      </Box>
      {openModal && (
        <LinkedDocumentModal
          open={openModal}
          setOpen={setOpenModal}
          onSubmit={handleModalSubmit}
          excludedIds={excludedIds}
          accept={PERMITTED_DOCUMENTS_TYPE}
          mimeTypes={PERMITTED_DOCUMENTS_MIME_TYPES}
          headerContent={
            <Alert severity="warning">
              Certifique-se que não existe nenhum dado pessoal no
              modelo/template selecionado! Lembre-se de que a existência de
              qualquer informação no documento é de responsabilidade do usuário.
            </Alert>
          }
        />
      )}
    </>
  )
}

DocumentsSection.propTypes = {
  dataProcessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
}

export default DocumentsSection
